// src/components/forms/UnitBulkForm.tsx — add a whole block of units at once.
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { initialsOf, useBuildingOptions, usePropertyOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select } from '../ui';
import { FormError, FormGrid, numberOr, toMinor } from './common';
import { UNIT_TYPES } from './UnitForm';

type Row = { key: number; unitType: string; bedrooms: string; bathrooms: string; areaSqft: string; rent: string; count: string };

const row = (key: number, over: Partial<Row> = {}): Row => ({ key, unitType: 'APARTMENT', bedrooms: '1', bathrooms: '1', areaSqft: '560', rent: '', count: '2', ...over });
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A 48-unit block is not 48 forms. Describe the floors and the mix per floor, see
 * exactly what will be created, then send it as one transaction — so a duplicate
 * reference half way through leaves nothing behind.
 */
export function UnitBulkForm({ open, onClose, propertyId }: { open: boolean; onClose: () => void; propertyId?: string }) {
  const qc = useQueryClient();
  const properties = usePropertyOptions();
  const [form, setForm] = useState({ propertyId: propertyId ?? '', buildingId: '', prefix: '', floorFrom: '1', floorTo: '4', startNumber: '1', currency: 'GBP' });
  const [mix, setMix] = useState<Row[]>([row(1), row(2, { unitType: 'APARTMENT', bedrooms: '2', bathrooms: '2', areaSqft: '820', count: '2' })]);
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const buildings = useBuildingOptions(form.propertyId);

  const property = (properties.data ?? []).find((p) => p.id === form.propertyId);
  const prefix = form.prefix.trim() || initialsOf(property?.name);

  // The rows that will actually be posted. Recomputed as you type, so the preview
  // below is the payload rather than a description of it.
  const units = useMemo(() => {
    const from = numberOr(form.floorFrom, 1)!;
    const to = numberOr(form.floorTo, 1)!;
    const start = numberOr(form.startNumber, 1)!;
    if (!form.propertyId || to < from) return [];
    const out: Array<Record<string, unknown>> = [];
    for (let floor = from; floor <= to; floor += 1) {
      let n = start;
      for (const r of mix) {
        for (let i = 0; i < (numberOr(r.count, 0) ?? 0); i += 1) {
          out.push({
            propertyId: form.propertyId,
            buildingId: form.buildingId || undefined,
            reference: `${prefix}-${pad(floor)}-${pad(n)}`,
            floor,
            bedrooms: numberOr(r.bedrooms, 1),
            bathrooms: numberOr(r.bathrooms, 1),
            areaSqft: numberOr(r.areaSqft) ?? null,
            unitType: r.unitType,
            status: 'VACANT',
            marketRentMinor: toMinor(r.rent) ?? 0,
            marketRentCurrency: form.currency,
          });
          n += 1;
        }
      }
    }
    return out;
  }, [form, mix, prefix]);

  const create = useMutation({
    mutationFn: async () => (await api.post<Array<Record<string, unknown>>>('/units/bulk', { units })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['/units'] });
      void qc.invalidateQueries({ queryKey: ['/properties'] });
      void qc.invalidateQueries({ queryKey: ['property-avail'] });
      onClose();
    },
  });

  const tooMany = units.length > 250;
  const perFloor = mix.reduce((s, r) => s + (numberOr(r.count, 0) ?? 0), 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Add units in bulk"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => create.mutate()} disabled={!units.length || tooMany || create.isPending}>{create.isPending ? `Creating ${units.length}…` : `Create ${units.length} unit${units.length === 1 ? '' : 's'}`}</Button>
      </>}
    >
      <div className="space-y-5">
        <FormGrid>
          <Field label="Scheme">
            <Select value={form.propertyId} disabled={!!propertyId} onChange={(e) => set({ propertyId: e.target.value, buildingId: '' })} className="w-full">
              <option value="">Choose a scheme…</option>
              {(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Building"><Select value={form.buildingId} onChange={(e) => set({ buildingId: e.target.value })} className="w-full" disabled={!form.propertyId}><option value="">None</option>{(buildings.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>

          <Field label="Reference prefix" hint={`References look like ${prefix}-01-01`}><Input value={form.prefix} onChange={(e) => set({ prefix: e.target.value })} placeholder={initialsOf(property?.name)} className="w-full" /></Field>
          <Field label="Currency"><Select value={form.currency} onChange={(e) => set({ currency: e.target.value })} className="w-full"><option value="GBP">GBP</option><option value="AED">AED</option></Select></Field>

          <Field label="Floors from"><Input value={form.floorFrom} onChange={(e) => set({ floorFrom: e.target.value })} inputMode="numeric" className="w-full" /></Field>
          <Field label="Floors to"><Input value={form.floorTo} onChange={(e) => set({ floorTo: e.target.value })} inputMode="numeric" className="w-full" /></Field>
        </FormGrid>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-ink-600">Mix per floor <span className="text-ink-400">· {perFloor} unit{perFloor === 1 ? '' : 's'} on each</span></span>
            <button type="button" onClick={() => setMix((m) => [...m, row(Date.now())])} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add a type</button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-ink-200">
            <table className="w-full min-w-max text-[12.5px]">
              <thead><tr className="border-b border-ink-100 bg-ink-50/70 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">
                <th className="px-3 py-2">Type</th><th className="px-3 py-2">Beds</th><th className="px-3 py-2">Baths</th><th className="px-3 py-2">Sq ft</th><th className="px-3 py-2">Rent pcm</th><th className="px-3 py-2">Per floor</th><th className="px-3 py-2" />
              </tr></thead>
              <tbody>
                {mix.map((r, i) => (
                  <tr key={r.key} className="border-b border-ink-100 last:border-0">
                    <td className="px-2 py-1.5"><Select value={r.unitType} onChange={(e) => setMix((m) => m.map((x, j) => j === i ? { ...x, unitType: e.target.value } : x))} className="w-32 !py-1.5 !text-[12px]">{UNIT_TYPES.map((t) => <option key={t} value={t}>{t[0] + t.slice(1).toLowerCase()}</option>)}</Select></td>
                    {(['bedrooms', 'bathrooms', 'areaSqft', 'rent', 'count'] as const).map((field) => (
                      <td key={field} className="px-2 py-1.5">
                        <Input value={r[field]} inputMode="decimal" onChange={(e) => setMix((m) => m.map((x, j) => j === i ? { ...x, [field]: e.target.value } : x))} className="w-20 !py-1.5 !text-[12px]" />
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right">{mix.length > 1 && <button type="button" onClick={() => setMix((m) => m.filter((_, j) => j !== i))} className="rounded-full p-1.5 text-ink-300 transition hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {units.length > 0 && (
          <div className="rounded-xl border border-ink-200 bg-ink-50/50 px-4 py-3">
            <p className="text-[12px] font-medium text-ink-700">{units.length} units will be created, all vacant</p>
            <p className="mt-1.5 font-mono text-[11.5px] leading-relaxed text-ink-500">
              {units.slice(0, 6).map((u) => String(u.reference)).join(' · ')}{units.length > 7 ? ` … ${String(units[units.length - 1]!.reference)}` : ''}
            </p>
            {tooMany && <p className="mt-2 text-[12px] text-red-600">That is more than 250 units in one go. Split it across two runs.</p>}
          </div>
        )}

        <FormError error={create.error} />
      </div>
    </Modal>
  );
}
